<script setup lang="ts">
import type { TOrientation } from './BaseTabView.vue'
import BoosterImage from '#booster/components/BoosterImage.vue'

type TItem = {
  navTitle: string
  title: string
  description: string
  linkText: string
  linkUrl: string
  isLinkExternal: boolean
  imageSrc: string
}

type TProps = TBuilderBaseProps & {
  items?: TItem[]
  orientation?: TOrientation
}

const props = withDefaults(defineProps<TProps>(), {
  items: () => [],
  orientation: 'horizontal',
  attributes: () => ({}),
})
</script>

<template>
  <LazyBaseTabView
    v-if="props.items.length > 0"
    v-slot="{ currentActiveItem }"
    hydrate-on-visible
    :items="props.items"
    :orientation="props.orientation"
  >
    <LazyBaseTabViewContent
      v-for="(item, index) in props.items"
      :key="`tab-hero-like-content-${index}-${item.navTitle}`"
      :is-active="currentActiveItem === index"
    >
      <div
        class="w-full lg:pb-24 inline-flex rtl:flex-reverse justify-center items-center"
      >
        <div
          class="inline-flex flex-col lg:flex-row flex-1 justify-center items-center gap-12 lg:gap-24"
        >
          <div
            class="h-full max-h-full lg:w-full lg:max-w-full py-2 inline-flex flex-col justify-center items-start gap-2"
          >
            <div
              class="self-stretch flex flex-col justify-center items-start gap-4"
            >
              <h3
                class="justify-center text-trail-text-display-normal text-trail-display2 md:text-trail-display1"
              >
                {{ item.title }}
              </h3>

              <div
                class="self-stretch justify-center text-trail-text-body-normal text-trail-body2"
              >
                {{ item.description }}
              </div>

              <div
                v-if="item.linkText"
                class="justify-center text-trail-text-body-normal text-trail-body-link2"
              >
                <a
                  class="underline"
                  :href="item.linkUrl"
                  :rel="item.isLinkExternal ? 'noopener noreferrer' : ''"
                  :target="item.isLinkExternal ? '_blank' : '_self'"
                  :title="item.linkText"
                >
                  <TrailDSButton>{{ item.linkText }}</TrailDSButton>
                </a>
              </div>
            </div>
          </div>

          <div
            v-if="item.imageSrc"
            class="w-full flex justify-start items-center gap-2"
          >
            <BoosterImage
              :quality="80"
              placeholder
              :src="item.imageSrc"
              :title="`Image ${item.title}`"
              :alt="`Image ${item.title}`"
              :aria-label="`Image ${item.title}`"
              class="flex-1 w-full h-auto rounded-2xl object-fill"
              provider="https://cdn.builder.io"
              loading="lazy"
              loading-spinner
            />
          </div>
        </div>
      </div>
    </LazyBaseTabViewContent>
  </LazyBaseTabView>
</template>
